import {RPCService} from '../service/rpc-service';
import {
    CameraBrandModel,
    CameraModel,
    ControlModel,
    ControlTypeModel,
    InputModel, InputTypeModel, MediaModel,
    SensorTypeModel,
    UserModel,
} from '../types/control';
import {BridgeDeviceModel} from '../types/bridge-device';
import {
    AlarmStateModel,
    AreaModel,
    ConfigModel,
    MediaInputModel, VirtualControlModel,
    ScenarioModel,
    ScenarioTriggerModel, HomepageItemModel, SoundState, MenuEntity, STPUIEntity, LanguageModel,
} from '../store/control/state';
import {DoorModel} from '../types/door';
import {IntercomModel} from '../types/intercom';
import {SettingsModel} from '../store/settings/state';
import {setDefaultAlarmStateAction, setEntityListAction, setSoundStateAction} from '../store/control/actions';
import {setSettingsAction} from '../store/settings/actions';
import {Dispatch} from 'react';
import {Action} from '../base/fsa/createAction';
import {batch} from 'react-redux';
import {setApplicationLoaded, setMediaFileListAction} from '../store/ui/actions';
import {MediaFileModel} from '../store/ui/state';
import {GatewayModel} from '../types/gateway';
import {DoorBrandModel} from '../types/door-brand';
import {BridgeDeviceTypeModel} from '../types/bridge-device-type';
import {LocationModel} from '../types/location';
import {setTemperatureAction} from '../store/temperature/action';
import {TemperatureMessage} from '../store/temperature/state';
import i18n from 'i18next';
import {ConciergeModel} from '../types/concierge';
import {ConciergeTypeModel} from '../types/concierge-type';

let latestVersion = null;

export async function fetchAndInitialize(rpc_service: RPCService, dispatch: Dispatch<Action>) {
    const sensorTypePromise = rpc_service.getSensorTypeList();
    const controlTypePromise = rpc_service.getControlTypeList();
    const userPromise = rpc_service.getUserList();
    const controlPromise = rpc_service.getControlList();
    const bridgeDevicePromise = rpc_service.getBridgeDeviceList();
    const bridgeDeviceTypePromise = rpc_service.getBridgeDeviceTypeList();
    const gatewayPromise = rpc_service.getGatewayList();
    const cameraPromise = rpc_service.getCameraList();
    const cameraBrandPromise = rpc_service.getCameraBrandList();
    const mediaFilePromise = rpc_service.getMediaFileList();
    const inputTypePromise = rpc_service.getInputTypeList();
    const inputPromise = rpc_service.getInputList();
    const scenarioPromise = rpc_service.getScenarioList();
    const scenarioTriggerPromise = rpc_service.getScenarioTriggerList();
    const doorPromise = rpc_service.getDoorList();
    const doorBrandPromise = rpc_service.getDoorBrandList();
    const intercomPromise = rpc_service.getIntercomList();
    const areaPromise = rpc_service.getAreaList();
    const mediaPromise = rpc_service.getMediaList();
    const mediaInputPromise = rpc_service.getMediaInputList();
    const virtualControlPromise = rpc_service.getVirtualControlList();
    const homepageItemPromise = rpc_service.getHomepageItemList();
    const alarmStatePromise = rpc_service.getAlarmState();
    const soundStatePromise = rpc_service.getSoundState();
    const settingsPromise = rpc_service.getSettings();
    const configsPromise = rpc_service.getConfigs();
    const locationPromise = rpc_service.getLocationList();
    const temperaturePromise = rpc_service.getTemperature();
    const menuItemPromise = rpc_service.getMenuList();
    const stpUIPromise = rpc_service.getSTPUIList();
    const languagePromise = rpc_service.getLanguageList();
    const conciergePromise = rpc_service.getConciergeList();
    const conciergeTypePromise = rpc_service.getConciergeTypeList();

    const [
        sensorTypes,
        controlTypes,
        users,
        controls,
        bridgeDevices,
        bridgeDeviceTypes,
        gateways,
        cameras,
        cameraBrands,
        mediaFiles,
    ] = await Promise.all<SensorTypeModel[],
        ControlTypeModel[],
        UserModel[],
        ControlModel[],
        BridgeDeviceModel[],
        BridgeDeviceTypeModel[],
        GatewayModel[],
        CameraModel[],
        CameraBrandModel[],
        MediaFileModel[]>([
        sensorTypePromise,
        controlTypePromise,
        userPromise,
        controlPromise,
        bridgeDevicePromise,
        bridgeDeviceTypePromise,
        gatewayPromise,
        cameraPromise,
        cameraBrandPromise,
        mediaFilePromise,
    ]);

    const [
        inputTypes,
        inputs,
        scenarios,
        scenarioTriggers,
        doors,
        doorBrands,
        intercoms,
        areas,
        medias,
        mediaInputs,
    ] = await Promise.all<InputTypeModel[],
        InputModel[],
        ScenarioModel[],
        ScenarioTriggerModel[],
        DoorModel[],
        DoorBrandModel[],
        IntercomModel[],
        AreaModel[],
        MediaModel[],
        MediaInputModel[]>([
        inputTypePromise,
        inputPromise,
        scenarioPromise,
        scenarioTriggerPromise,
        doorPromise,
        doorBrandPromise,
        intercomPromise,
        areaPromise,
        mediaPromise,
        mediaInputPromise,
    ]);

    const [
        virtualControls,
        homepageItems,
        alarmState,
        soundState,
        settings,
        configs,
        locations,
        temperatureResponse,
        menuItems,
        stpUIs,
    ] = await Promise.all<VirtualControlModel[],
        HomepageItemModel[],
        AlarmStateModel,
        SoundState,
        SettingsModel,
        ConfigModel,
        LocationModel[],
        TemperatureMessage,
        MenuEntity[],
        STPUIEntity[]>([
        virtualControlPromise,
        homepageItemPromise,
        alarmStatePromise,
        soundStatePromise,
        settingsPromise,
        configsPromise,
        locationPromise,
        temperaturePromise,
        menuItemPromise,
        stpUIPromise,
    ]);

    const [
        languages,
        concierges,
        conciergeTypes,
    ] = await Promise.all<LanguageModel[],
        ConciergeModel[],
        ConciergeTypeModel[]>([
        languagePromise,
        conciergePromise,
        conciergeTypePromise,
    ]);

    for (const lang of languages) {
        localStorage.setItem(`lang_${lang.id}`, JSON.stringify(lang.variables));
        i18n.addResources(lang.id, 'translations', lang.variables);
    }

    if (await shouldReloadPage(rpc_service)) {
        window.location.reload();
        return;
    }

    batch(() => {

        dispatch(setEntityListAction(sensorTypes, 'sensorType'));
        dispatch(setEntityListAction(controlTypes, 'controlType'));
        dispatch(setEntityListAction(users, 'user'));
        dispatch(setEntityListAction(controls, 'control'));
        dispatch(setEntityListAction(bridgeDevices, 'bridgeDevice'));
        dispatch(setEntityListAction(bridgeDeviceTypes, 'bridgeDeviceType'));
        dispatch(setEntityListAction(gateways, 'gateway'));
        dispatch(setEntityListAction(cameras, 'camera'));
        dispatch(setEntityListAction(cameraBrands, 'cameraBrand'));
        dispatch(setEntityListAction(inputTypes, 'inputType'));
        dispatch(setEntityListAction(inputs, 'input'));
        dispatch(setEntityListAction(scenarios, 'scenario'));
        dispatch(setEntityListAction(scenarioTriggers, 'scenarioTrigger'));
        dispatch(setEntityListAction(doors, 'door'));
        dispatch(setEntityListAction(doorBrands, 'doorBrand'));
        dispatch(setEntityListAction(intercoms, 'intercom'));
        dispatch(setEntityListAction(areas, 'area'));
        dispatch(setEntityListAction(medias, 'media'));
        dispatch(setEntityListAction(mediaInputs, 'mediaInput'));
        dispatch(setEntityListAction(virtualControls, 'virtualControl'));
        dispatch(setEntityListAction(homepageItems, 'homepageItem'));
        dispatch(setEntityListAction(configs, 'config'));
        dispatch(setEntityListAction(locations, 'location'));
        dispatch(setEntityListAction(stpUIs, 'stpUI'));
        dispatch(setEntityListAction(languages, 'language'));
        dispatch(setEntityListAction(concierges, 'concierge'));
        dispatch(setEntityListAction(conciergeTypes, 'conciergeType'));

        if (temperatureResponse != null) {
            dispatch(setTemperatureAction(temperatureResponse.icon, temperatureResponse.temperature));
        }
        dispatch(setEntityListAction(menuItems, 'menu'));

        dispatch(setDefaultAlarmStateAction(alarmState));
        dispatch(setSoundStateAction(soundState));
        dispatch(setSettingsAction(settings));
        dispatch(setMediaFileListAction(mediaFiles || []));
        dispatch(setApplicationLoaded());
    });
}


function shouldReloadPage(rpc_service: RPCService) {
    return rpc_service.getSystemInfo()
        .then(systemInfo => {
            if (latestVersion != null && systemInfo.version !== latestVersion) {
                return true;
            }

            latestVersion = systemInfo.version;
            return false;
        });
}
